// src/algorithms/aiCodingImpact.ts
import { Project } from '../types';
import { mockProjects } from '../data/mockData';

export interface AICodingImpactInput {
  projectIds: string[];
  timeframe: '1-month' | '3-months' | '6-months' | 'year';
  metrics: Array<'productivity' | 'quality' | 'velocity' | 'token-usage'>;
  comparison: 'with-vs-without' | 'team-a-b' | 'pre-post';
}

export interface AICodingImpactOutput {
  summary: {
    overallImpact: number;
    confidence: number;
    recommendation: 'expand' | 'maintain' | 'reduce' | 'investigate';
  };
  productivityAnalysis: {
    linesOfCodePerHour: { withAI: number; withoutAI: number };
    completionTimeImprovement: number;
    developerSentiment: 'positive' | 'neutral' | 'negative';
  };
  qualityMetrics: {
    defectRate: { withAI: number; withoutAI: number };
    codeReviewTime: { withAI: string; withoutAI: string };
    pullRequestSize: { withAI: number; withoutAI: number };
    complexityScore: { withAI: number; withoutAI: number };
  };
  tokenUsage: {
    averageTokensPerDay: number;
    costPerDeveloper: number;
    roi: number;
    optimizationOpportunities: Array<{
      pattern: string;
      impact: number;
      recommendation: string;
    }>;
  };
  teamImpact: {
    skillLevelImpact: Array<{
      skillLevel: 'junior' | 'mid' | 'senior';
      productivityGain: number;
      qualityChange: number;
    }>;
    adoptionRate: number;
    learningCurve: string;
  };
  recommendations: Array<{
    priority: 'high' | 'medium' | 'low';
    action: string;
    expectedImpact: string;
    effort: 'low' | 'medium' | 'high';
  }>;
  visualizationData: {
    productivityTrend: Array<{ week: number; withAI: number; withoutAI: number }>;
    defectTrend: Array<{ week: number; withAI: number; withoutAI: number }>;
    tokenUsageCorrelation: Array<{ tokens: number; qualityScore: number }>;
  };
}

// Mock data for AI coding impact
const mockAIData = {
  projectsWithAI: ['PROJ_001', 'PROJ_002'],
  projectsWithoutAI: ['PROJ_003', 'PROJ_004'],
  developerAIUsage: {
    'EMP_101': { hasAccess: true, dailyTokens: 1500, adoptionScore: 85 },
    'EMP_102': { hasAccess: true, dailyTokens: 2200, adoptionScore: 92 },
    'EMP_103': { hasAccess: false, dailyTokens: 0, adoptionScore: 0 },
    'EMP_104': { hasAccess: true, dailyTokens: 1800, adoptionScore: 78 },
    'EMP_105': { hasAccess: false, dailyTokens: 0, adoptionScore: 0 },
  },
  historicalMetrics: {
    productivity: [
      { week: 1, withAI: 65, withoutAI: 60 },
      { week: 2, withAI: 68, withoutAI: 61 },
      { week: 3, withAI: 72, withoutAI: 59 },
      { week: 4, withAI: 75, withoutAI: 62 },
      { week: 5, withAI: 78, withoutAI: 63 },
      { week: 6, withAI: 80, withoutAI: 64 },
      { week: 7, withAI: 82, withoutAI: 65 },
      { week: 8, withAI: 85, withoutAI: 66 },
      { week: 9, withAI: 83, withoutAI: 64 },
      { week: 10, withAI: 87, withoutAI: 65 },
      { week: 11, withAI: 88, withoutAI: 66 },
      { week: 12, withAI: 90, withoutAI: 67 },
    ],
    defects: [
      { week: 1, withAI: 12, withoutAI: 15 },
      { week: 2, withAI: 10, withoutAI: 14 },
      { week: 3, withAI: 8, withoutAI: 16 },
      { week: 4, withAI: 9, withoutAI: 13 },
      { week: 5, withAI: 7, withoutAI: 14 },
      { week: 6, withAI: 6, withoutAI: 15 },
      { week: 7, withAI: 5, withoutAI: 12 },
      { week: 8, withAI: 4, withoutAI: 11 },
      { week: 9, withAI: 5, withoutAI: 13 },
      { week: 10, withAI: 4, withoutAI: 10 },
      { week: 11, withAI: 3, withoutAI: 9 },
      { week: 12, withAI: 3, withoutAI: 8 },
    ],
  },
};

export function analyzeAICodingImpact(
  input: AICodingImpactInput
): AICodingImpactOutput {
  // Calculate productivity metrics
  const productivityGain = calculateProductivityGain(input.timeframe);
  const qualityImprovement = calculateQualityImprovement();
  const tokenAnalysis = analyzeTokenUsage();
  const teamImpact = analyzeTeamImpact();
  
  // Determine overall impact and recommendation
  const overallImpact = Math.round((productivityGain * 0.4 + qualityImprovement * 0.3 + tokenAnalysis.roi * 0.3) * 10) / 10;
  
  let recommendation: 'expand' | 'maintain' | 'reduce' | 'investigate' = 'maintain';
  if (overallImpact > 20) recommendation = 'expand';
  else if (overallImpact > 10) recommendation = 'maintain';
  else if (overallImpact > 5) recommendation = 'investigate';
  else recommendation = 'reduce';
  
  return {
    summary: {
      overallImpact,
      confidence: 87,
      recommendation,
    },
    productivityAnalysis: {
      linesOfCodePerHour: { withAI: 45, withoutAI: 32 },
      completionTimeImprovement: 28,
      developerSentiment: 'positive',
    },
    qualityMetrics: {
      defectRate: { withAI: 2.8, withoutAI: 4.2 },
      codeReviewTime: { withAI: '3.2h', withoutAI: '4.8h' },
      pullRequestSize: { withAI: 220, withoutAI: 180 },
      complexityScore: { withAI: 3.2, withoutAI: 3.8 },
    },
    tokenUsage: tokenAnalysis,
    teamImpact,
    recommendations: generateRecommendations(overallImpact, teamImpact, tokenAnalysis),
    visualizationData: {
      productivityTrend: mockAIData.historicalMetrics.productivity,
      defectTrend: mockAIData.historicalMetrics.defects,
      tokenUsageCorrelation: [
        { tokens: 1000, qualityScore: 78 },
        { tokens: 1500, qualityScore: 82 },
        { tokens: 2000, qualityScore: 85 },
        { tokens: 2500, qualityScore: 81 },
        { tokens: 3000, qualityScore: 76 },
        { tokens: 3500, qualityScore: 70 },
      ],
    },
  };
}

// Helper functions
function calculateProductivityGain(timeframe: string): number {
  const baseGain = 25; // 25% improvement
  let timeframeMultiplier = 1;
  
  switch (timeframe) {
    case '1-month': timeframeMultiplier = 0.8; break;
    case '3-months': timeframeMultiplier = 1; break;
    case '6-months': timeframeMultiplier = 1.2; break;
    case 'year': timeframeMultiplier = 1.5; break;
  }
  
  return Math.round(baseGain * timeframeMultiplier);
}

function calculateQualityImprovement(): number {
  const defectReduction = ((4.2 - 2.8) / 4.2) * 100; // 33.3%
  const reviewTimeReduction = 33; // hours
  const complexityImprovement = 15; // percentage
  
  return Math.round((defectReduction * 0.5 + reviewTimeReduction * 0.3 + complexityImprovement * 0.2));
}

function analyzeTokenUsage() {
  const developersWithAI = Object.values(mockAIData.developerAIUsage).filter(d => d.hasAccess);
  const avgTokens = developersWithAI.reduce((sum: number, d: any) => sum + d.dailyTokens, 0) / developersWithAI.length;
  const costPerDeveloper = avgTokens * 30 * 0.002; // $0.002 per 1K tokens
  
  const productivityGain = 28; // percentage
  const developerCost = 10000; // average monthly cost
  const valueGained = developerCost * (productivityGain / 100);
  const roi = ((valueGained - costPerDeveloper) / costPerDeveloper) * 100;
  
  return {
    averageTokensPerDay: Math.round(avgTokens),
    costPerDeveloper: Math.round(costPerDeveloper * 100) / 100,
    roi: Math.round(roi * 10) / 10,
    optimizationOpportunities: [
      {
        pattern: "High token usage for boilerplate code",
        impact: 15,
        recommendation: "Create custom code snippets",
      },
      {
        pattern: "Over-reliance on AI for complex logic",
        impact: 25,
        recommendation: "Balance with code reviews",
      },
      {
        pattern: "Duplicate AI-generated code",
        impact: 10,
        recommendation: "Implement shared patterns library",
      },
    ],
  };
}

function analyzeTeamImpact() {
  const juniorImpact = { skillLevel: 'junior' as const, productivityGain: 35, qualityChange: 15 };
  const midImpact = { skillLevel: 'mid' as const, productivityGain: 25, qualityChange: 10 };
  const seniorImpact = { skillLevel: 'senior' as const, productivityGain: 15, qualityChange: 5 };
  
  const adoptionRate = Math.round(
    (Object.values(mockAIData.developerAIUsage).filter(d => d.hasAccess).length / 
     Object.values(mockAIData.developerAIUsage).length) * 100
  );
  
  return {
    skillLevelImpact: [juniorImpact, midImpact, seniorImpact],
    adoptionRate,
    learningCurve: '2-4 weeks',
  };
}

function generateRecommendations(
  overallImpact: number,
  teamImpact: any,
  tokenAnalysis: any
) {
  const recommendations = [];
  
  if (overallImpact > 20) {
    recommendations.push({
      priority: 'high' as const,
      action: 'Expand AI tool access to all development teams',
      expectedImpact: 'Increase overall productivity by additional 5-10%',
      effort: 'low',
    });
  }
  
  if (teamImpact.adoptionRate < 80) {
    recommendations.push({
      priority: 'medium' as const,
      action: 'Run training sessions on effective AI tool usage',
      expectedImpact: 'Increase adoption rate by 15-20%',
      effort: 'medium',
    });
  }
  
  if (tokenAnalysis.roi < 200) {
    recommendations.push({
      priority: 'medium' as const,
      action: 'Optimize token usage patterns identified in analysis',
      expectedImpact: `Improve ROI by ${tokenAnalysis.optimizationOpportunities.reduce((sum: number, o: any) => sum + o.impact, 0)}%`,
      effort: 'medium',
    });
  }
  
  // Always include these recommendations
  recommendations.push({
    priority: 'low' as const,
    action: 'Monitor AI-generated code quality monthly',
    expectedImpact: 'Maintain quality standards as usage scales',
    effort: 'low',
  });
  
  recommendations.push({
    priority: 'medium' as const,
    action: 'Create centralized repository for AI-generated patterns',
    expectedImpact: 'Reduce duplicate code generation by 30%',
    effort: 'medium',
  });
  
  return recommendations;
}